import { fetchFolder, getItemFromCollection } from "directus/src/operations";
import { useQueries, useQuery } from "@tanstack/react-query";

export function useItemById(collection, id, options = {}) {
  return useQuery({
    queryKey: [collection, "id", id],
    queryFn: async () => {
      const query = {
        fields: ["name"],
      };
      const response = await getItemFromCollection(collection, id, query);
      return response;
    },
    enabled: !!(collection && id),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useFolderById(id, options = {}) {
  return useQuery({
    queryKey: ["folders", "id", id],
    queryFn: async () => {
      const response = await fetchFolder(id, {
        fields: ["name"],
      });
      return response;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetches folder names for each ID in the path (splat).
 * Use for breadcrumbs when the route is organization/folders/* with segments like id1/id2/id3.
 * @param {string[]} folderIds - Array of folder IDs (e.g. from splat.split("/"))
 * @returns {{ data: Array<{ id: string, name: string }>, isLoading: boolean, error: Error | null }}
 */
export function useFoldersByIds(folderIds) {
  const queries = useQueries({
    queries: (folderIds || []).map((id) => ({
      queryKey: ["folders", "id", id],
      queryFn: async () => {
        const response = await fetchFolder(id, { fields: ["name"] });
        return { id, name: response?.name ?? id };
      },
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    })),
  });

  const data = queries
    .filter((q) => q.data)
    .map((q) => q.data);

  const isLoading = queries.some((q) => q.isLoading);
  const error = queries.find((q) => q.error)?.error ?? null;

  const ids = folderIds ?? [];
  return {
    data: ids.length === 0 ? [] : data.length === ids.length ? data : undefined,
    isLoading,
    error,
  };
}